import React, { useCallback, useEffect, useState } from "react"
import {
  Alert,
  Card,
  CardBody,
  Col,
  Container,
  Form,
  Input,
  Label,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
  Toast,
  ToastBody,
  ToastHeader,
} from "reactstrap"
import classnames from "classnames"
import { Link } from "react-router-dom"
import logoSm from "../../assets/images/logo-sm.png"
import Loader from "../../components/Common/Loader"
import { emailRegex } from "../../constants/constants"
import countriesJson from "../../common/data/countries.json"
import emailjs from "@emailjs/browser"
import { startCase } from "lodash"
import { FaInfoCircle } from "react-icons/fa"

const initialData = {
  firstName: "",
  lastName: "",
  middleName: "",
  email: "",
  phoneNumber: "",
  gender: "",
  address: "",
  nationality: "nigeria",
  location: "mini_avenue",
  appointmentType: "consultation",
  serviceText: "",
  date: "",
  time: "",
  description: "",
  age: "",
  hmo: "",
  hmoName: "",
  hmoCode: "",
}

const initialMessage = {
  error: null,
  warning: null,
  success: null,
}

const servicesOptions = [
  "Mental Health Rehabilitation",
  "Drug Rehabilitation",
  "Speech Therapy",
  "Music Therapy",
  "Psychotherapy",
  "Community Rehabilitation",
  "Occupational Therapy",
  "Sheltered Employment",
  "Social Services",
  "Other",
]

const EMAILJS_CONFIG = {
  SERVICE_ID: "service_wpbqdkc",
  TEMPLATE_ID_CLINIC: "template_eqe3mza",
  TEMPLATE_ID_USER: "template_xuhf1gv",
  PUBLIC_KEY: "n0NgBG_otoDG9LlFm",
}

const LOCATION_EMAILS = {
  mini_avenue: "prestige@elpidarehab.com",
  jofil_close: "proseniore@elpidarehab.com",
}

const LOCATION_ADDRESSES = {
  mini_avenue:
    "ELPIDA Prestige, Centre For Drug & Other Neurological Rehabilitation, No 12 Mini Avenue, beside Emsoch Emirates Hotel, Off Odaraware Road, off NTA Road, Port Harcourt, Rivers State.",
  jofil_close:
    "ELPIDA Pro Seniore, Centre For Older Persons & Lifestyle Medicine, 2 Jofil Close, Beside Love Channel Church, Opposite Elmer, Supermarket. Arcania bend, Ada George Road, Port Harcourt.",
}

const AppointmentsPublic = () => {
  document.title = "Appointments | Elpida Rehab"
  const [activeTab, setactiveTab] = useState(1)
  const [filledTabs, setFilledTabs] = useState(1)
  const [data, setData] = useState(initialData)
  const [submitted, setSubmitted] = useState(false)
  const [username, setUsername] = useState("")
  const [message, setMessage] = useState(initialMessage)
  const [toasts, setToasts] = useState([])
  const [loading, setLoading] = useState(false)
  const [consentToMarketing, setConsentToMarketing] = useState(true)

  function toggleTab(tab) {
    if (activeTab !== tab) {
      if (tab >= 1 && tab <= 3) {
        setactiveTab(tab)
      }
    }
    if (tab > filledTabs) {
      setFilledTabs(tab)
    }
  }

  const addToast = newMessage => {
    const newToast = { id: toasts.length, ...newMessage }
    setToasts([...toasts, newToast])

    setTimeout(() => {
      setToasts(prevToasts =>
        prevToasts.filter(toast => toast.id !== newToast.id),
      )
    }, 3000)
  }

  const changeData = prop => {
    setData({ ...data, ...prop })
  }

  const setDisplayName = useCallback(() => {
    const currentUsername = `${data?.firstName} ${data.lastName}`
    if (window && currentUsername !== username) {
      const existingAuthUser = JSON.parse(localStorage.getItem("authUser"))
      localStorage.setItem(
        "authUser",
        JSON.stringify({
          ...existingAuthUser,
          displayName: currentUsername,
        }),
      )
      setUsername(currentUsername)
    }
  }, [data, username])

  useEffect(() => {
    setDisplayName()
  }, [setDisplayName])

  useEffect(() => {
    const sendHeight = () => {
      window.parent.postMessage(
        { type: "resize", height: document.body.scrollHeight },
        "*",
      )
    }
    sendHeight()
    window.addEventListener("resize", sendHeight)
    return () => window.removeEventListener("resize", sendHeight)
  }, [])

  const getLocationName = loc => {
    switch (loc?.toLowerCase()) {
      case "mini_avenue":
        return "ELPIDA Prestige"
      case "jofil_close":
        return "ELPIDA Pro Seniore"
      default:
        return ""
    }
  }

  const doSubmit = async () => {
    try {
      resetMessage()
      setLoading(true)

      emailjs.init(EMAILJS_CONFIG.PUBLIC_KEY)

      const hmoSection =
        data.hmoName && data.hmoCode
          ? ` <div class="info-label">HMO/Approval Code:</div>
              <div class="info-value">${data.hmoName?.toUpperCase()} / ${data.hmoCode
          }</div>`
          : ""

      const locationEmail =
        LOCATION_EMAILS[data.location] || LOCATION_EMAILS.mini_avenue

      const clinicTemplateParams = {
        to_email: locationEmail,
        clinic_location: getLocationName(data.location),
        to_name: startCase(data.firstName),
        patient_name: startCase(
          `${data.firstName} ${data.middleName || ""} ${data.lastName}`,
        ),
        patient_email: data.email,
        patient_phone: data.phoneNumber,
        patient_age: data.age || "Not provided",
        patient_gender: startCase(data.gender || "Not provided"),
        patient_nationality: startCase(data.nationality),
        appointment_type: startCase(data.appointmentType),
        service_requested: startCase(data.serviceText || "Not specified"),
        appointment_date: data.date,
        appointment_time: data.time,
        complaint: startCase(data.description || "None provided"),
        submission_date: new Date().toLocaleString(),
        clinic_email: locationEmail,
        clinic_phone: "+234 9029 406 760 / +234 7025 800 331",
        clinic_address: LOCATION_ADDRESSES[data.location] || LOCATION_ADDRESSES.mini_avenue,
        hmo: data.hmo === "yes" ? "Yes" : "No",
        hmo_section: hmoSection,
        marketing_consent: consentToMarketing
          ? "✅ Patient consented to receive marketing communications"
          : "❌ Patient opted out of marketing communications",
      }

      const userTemplateParams = {
        to_email: data.email,
        to_name: startCase(data.firstName),
        patient_name: startCase(`${data.firstName} ${data.lastName}`),
        clinic_location: getLocationName(data.location),
        appointment_type: startCase(data.appointmentType),
        service_requested: startCase(data.serviceText || "Not specified"),
        appointment_date: data.date,
        appointment_time: data.time,
        clinic_email: locationEmail,
        clinic_phone: "+234 9029 406 760 / +234 7025 800 331",
        clinic_address: LOCATION_ADDRESSES[data.location] || LOCATION_ADDRESSES.mini_avenue,
        hmo: data.hmo === "yes" ? "Yes" : "No",
        hmo_section: hmoSection,
        marketing_consent: consentToMarketing
          ? "You have opted in to receive appointment confirmations, reminders, and promotional communications."
          : "You have opted out of promotional communications. You will only receive essential appointment updates.",
      }

      const clinicResponse = await emailjs.send(
        EMAILJS_CONFIG.SERVICE_ID,
        EMAILJS_CONFIG.TEMPLATE_ID_CLINIC,
        clinicTemplateParams,
      )

      if (clinicResponse.status === 200) {
        const userResponse = await emailjs.send(
          EMAILJS_CONFIG.SERVICE_ID,
          EMAILJS_CONFIG.TEMPLATE_ID_USER,
          userTemplateParams,
        )
        if (userResponse.status === 200) {
          setSubmitted(true)
          setLoading(false)
          setMessage({
            success:
              "Appointment request sent successfully! Check your email for confirmation.",
          })
        } else {
          throw new Error("Failed to send confirmation email to user")
        }
      } else {
        throw new Error("Failed to send confirmation email to user")
      }
    } catch (error) {
      console.error("Email sending error:", error)
      setLoading(false)
      setMessage({
        error:
          error.text ||
          "Error sending appointment request. Please try again or contact us directly.",
      })
    }
  }

  const getMessageText = () => {
    if (message.error) {
      return { text: message.error, color: "danger" }
    } else if (message.warning) {
      return { text: message.warning, color: "warning" }
    } else if (message.success) {
      return { text: message.success, color: "success" }
    } else {
      return { text: "", color: "" }
    }
  }

  const resetMessage = () => {
    setMessage(initialMessage)
  }

  useEffect(() => {
    if (getMessageText().text) {
      setLoading(false)
      addToast({ content: getMessageText().text, type: getMessageText().color })
      setTimeout(() => {
        setMessage(initialMessage)
      }, 10000)
    }
    // eslint-disable-next-line
  }, [message])

  const levelVerified = () => {
    const {
      firstName,
      lastName,
      email,
      phoneNumber,
      appointmentType,
      date,
      time,
    } = data

    if (activeTab === 1) {
      if (data.hmo !== "yes") {
        changeData({ hmoName: "", hmoCode: "" })
      } else {
        if (!data.hmoName || !data.hmoCode) {
          setMessage({ warning: "Please fill all required fields!" })
          setLoading(false)
          return false
        }
      }
      if (!firstName || !lastName || !phoneNumber || !email) {
        setMessage({ warning: "Please fill all required fields!" })
        setLoading(false)
        return false
      }
      if (!emailRegex.test(email)) {
        setMessage({ warning: "Please enter a valid email address!" })
        setLoading(false)
        return false
      }
    }
    if (activeTab === 2) {
      if (!appointmentType || !date || !time) {
        setMessage({ warning: "Please fill all required fields!" })
        setLoading(false)
        return false
      }
    }
    resetMessage()
    return true
  }

  return (
    <React.Fragment>
      {loading ? (
        <Loader />
      ) : (
        <div className="page-content" style={{ padding: "1rem" }}>
          <Container fluid>
            <Row>
              <Col lg={12}>
                <Card>
                  <CardBody>
                    <div className="wizard clearfix">
                      {!submitted && (
                        <div className="steps clearfix cursor-pointer ">
                          <ul>
                            <NavItem
                              className={classnames({
                                current: activeTab === 1,
                              })}
                            >
                              <NavLink
                                className={classnames({
                                  current: activeTab === 1,
                                })}
                                onClick={() => {
                                  if (filledTabs >= 1 && !submitted) {
                                    setactiveTab(1)
                                  }
                                }}
                              >
                                <span className="number">1.</span>
                                Personal Information
                              </NavLink>
                            </NavItem>
                            <NavItem
                              className={classnames({
                                current: activeTab === 2,
                              })}
                            >
                              <NavLink
                                className={classnames({
                                  active: activeTab === 2,
                                })}
                                onClick={() => {
                                  if (filledTabs >= 2 && !submitted) {
                                    setactiveTab(2)
                                  }
                                }}
                              >
                                <span className="number">2.</span>
                                Booking Details
                              </NavLink>
                            </NavItem>
                            <NavItem
                              className={classnames({
                                current: activeTab === 3,
                              })}
                            >
                              <NavLink
                                className={classnames({
                                  active: activeTab === 3,
                                })}
                                onClick={() => {
                                  if (filledTabs >= 3 && !submitted) {
                                    if (levelVerified()) {
                                      setactiveTab(3)
                                    }
                                  }
                                }}
                              >
                                <span className="number">3.</span>
                                Confirmation
                              </NavLink>
                            </NavItem>
                          </ul>
                        </div>
                      )}
                      <div className="content clearfix">
                        <div className="body">
                          <TabContent activeTab={activeTab}>
                            <TabPane tabId={1}>
                              <Form style={{ minHeight: "200px" }}>
                                <Row>
                                  <Col md={4}>
                                    <div className="mb-3">
                                      <Label htmlFor="formrow-firstname-Input">
                                        First Name*
                                      </Label>
                                      <Input
                                        type="text"
                                        className="form-control"
                                        id="formrow-firstname-Input"
                                        value={data.firstName}
                                        onChange={e => {
                                          changeData({
                                            firstName: e.target?.value,
                                          })
                                        }}
                                      />
                                    </div>
                                  </Col>
                                  <Col md={4}>
                                    <div className="mb-3">
                                      <Label htmlFor="formrow-lastname-Input">
                                        Last Name*
                                      </Label>
                                      <Input
                                        type="text"
                                        className="form-control"
                                        id="formrow-lastname-Input"
                                        value={data.lastName}
                                        onChange={e => {
                                          changeData({
                                            lastName: e.target?.value,
                                          })
                                        }}
                                      />
                                    </div>
                                  </Col>
                                  <Col md={4}>
                                    <div className="mb-3">
                                      <Label htmlFor="formrow-age-Input">
                                        Age
                                      </Label>
                                      <input
                                        className="form-control"
                                        type="number"
                                        value={data.age}
                                        min={10}
                                        id="formrow-age-Input"
                                        onChange={e => {
                                          changeData({
                                            age: e.target?.value,
                                          })
                                        }}
                                      />
                                    </div>
                                  </Col>
                                </Row>

                                <Row>
                                  <Col md={8}>
                                    <div className="mb-3">
                                      <Label htmlFor="formrow-email-Input">
                                        Email*
                                      </Label>
                                      <Input
                                        type="email"
                                        className="form-control"
                                        id="formrow-email-Input"
                                        value={data.email}
                                        onChange={e => {
                                          changeData({
                                            email: e.target?.value,
                                          })
                                        }}
                                      />
                                    </div>
                                  </Col>
                                  <Col md={4}>
                                    <div className="mb-3">
                                      <Label htmlFor="formrow-phone-Input">
                                        Phone Number*
                                      </Label>
                                      <Input
                                        type="text"
                                        className="form-control"
                                        id="formrow-phone-Input"
                                        value={data.phoneNumber}
                                        onChange={e => {
                                          changeData({
                                            phoneNumber: e.target?.value,
                                          })
                                        }}
                                      />
                                    </div>
                                  </Col>
                                </Row>
                                <Row>
                                  <Col lg={4}>
                                    <div className="mb-3">
                                      <Label htmlFor="formrow-InputGender">
                                        Gender
                                      </Label>
                                      <select
                                        id="formrow-InputGender"
                                        className="form-control"
                                        value={data.gender}
                                        onChange={e => {
                                          changeData({
                                            gender: e.target?.value,
                                          })
                                        }}
                                      >
                                        <option value={""}>Choose...</option>
                                        <option value={"female"}>Female</option>
                                        <option value={"male"}>Male</option>
                                        <option value={"none"}>
                                          I prefer not to say.
                                        </option>
                                      </select>
                                    </div>
                                  </Col>
                                  <Col md={4}>
                                    <div className="mb-3">
                                      <Label htmlFor="formrow-InputNationality">
                                        Nationality
                                      </Label>
                                      <select
                                        id="formrow-InputNationality"
                                        className="form-control"
                                        value={data.nationality}
                                        onChange={e => {
                                          changeData({
                                            nationality: e.target?.value,
                                          })
                                        }}
                                      >
                                        <option value={"nigeria"}>
                                          Nigeria
                                        </option>
                                        {countriesJson
                                          ?.filter(c => c.value !== "nigeria")
                                          ?.map((c, i) => (
                                            <option key={i} value={c.value}>
                                              {c.label}
                                            </option>
                                          ))}
                                      </select>
                                    </div>
                                  </Col>
                                  <Col lg={4}>
                                    <div className="mb-3">
                                      <Label htmlFor="formrow-location">
                                        Preferred Location*
                                      </Label>
                                      <select
                                        id="formrow-location"
                                        className="form-control"
                                        value={data.location}
                                        onChange={e => {
                                          changeData({
                                            location: e.target?.value,
                                          })
                                        }}
                                      >
                                        {Object.entries(LOCATION_ADDRESSES).map(
                                          ([slug, address]) => (
                                            <option
                                              key={slug}
                                              value={slug}
                                              style={{
                                                whiteSpace: "normal",
                                              }}
                                            >
                                              {address}
                                            </option>
                                          ),
                                        )}
                                      </select>
                                    </div>
                                  </Col>
                                  <Col lg={4}>
                                    <div className="mb-3">
                                      <Label htmlFor="formrow-hmo-que">
                                        Do you have HMO cover *
                                      </Label>
                                      <select
                                        id="formrow-hmo-que"
                                        className="form-control"
                                        value={data.hmo}
                                        onChange={e => {
                                          changeData({
                                            hmo: e.target?.value,
                                          })
                                        }}
                                      >
                                        <option value={""}>Choose...</option>
                                        <option value={"yes"}>Yes</option>
                                        <option value={"no"}>No</option>
                                      </select>
                                    </div>
                                  </Col>
                                  {data?.hmo != "no" && data?.hmo !== "" && (
                                    <>
                                      <Col lg={4}>
                                        <div className="mb-3">
                                          <Label htmlFor="formrow-hmo-select">
                                            Select HMO *
                                          </Label>
                                          <select
                                            id="formrow-hmo-select"
                                            className="form-control"
                                            value={data.hmoName}
                                            defaultValue="Do you have HMO cover?"
                                            onChange={e => {
                                              changeData({
                                                hmoName: e.target?.value,
                                              })
                                            }}
                                          >
                                            <option value={""}>
                                              Select HMO
                                            </option>
                                            <option value={"axa mansard"}>
                                              AXA MANSARD
                                            </option>
                                            <option value={"leadway"}>
                                              {" "}
                                              LEADWAY
                                            </option>
                                            <option value={"reliance"}>
                                              {" "}
                                              RELIANCE
                                            </option>
                                            <option value={"philips hmo"}>
                                              {" "}
                                              PHILIPS HMO
                                            </option>
                                            <option value={"mso bupa"}>
                                              {" "}
                                              MSO BUPA
                                            </option>
                                          </select>
                                        </div>
                                      </Col>
                                      <Col md={4}>
                                        <div className="mb-3">
                                          <Label htmlFor="formrow-hmocode">
                                            HMO Code *
                                          </Label>
                                          <Input
                                            type="text"
                                            className="form-control"
                                            id="formrow-hmocode"
                                            value={data.hmoCode}
                                            onChange={e => {
                                              changeData({
                                                hmoCode: e.target?.value,
                                              })
                                            }}
                                          />
                                        </div>
                                      </Col>
                                    </>
                                  )}
                                </Row>
                              </Form>
                            </TabPane>
                            <TabPane tabId={2}>
                              <Form style={{ minHeight: "200px" }}>
                                <Row>
                                  <Col lg={12}>
                                    <div className="mb-3">
                                      <Label htmlFor="formrow-services">
                                        Consultation Category*
                                      </Label>
                                      <select
                                        id="formrow-services"
                                        className="form-control"
                                        value={data.serviceText}
                                        onChange={e => {
                                          changeData({
                                            serviceText: e.target?.value,
                                          })
                                        }}
                                      >
                                        <option value={""}>Choose...</option>
                                        {servicesOptions?.map((s, i) => {
                                          return (
                                            <option key={i} value={s}>
                                              {s}
                                            </option>
                                          )
                                        })}
                                      </select>
                                    </div>
                                  </Col>
                                </Row>

                                <Row>
                                  <Col md={4}>
                                    <div className="mb-3">
                                      <Label htmlFor="formrow-date-Input">
                                        Date*
                                      </Label>
                                      <input
                                        className="form-control"
                                        type="date"
                                        value={data.date}
                                        id="formrow-date-Input"
                                        min={
                                          new Date().toISOString().split("T")[0]
                                        }
                                        onChange={e => {
                                          changeData({ date: e.target?.value })
                                        }}
                                      />
                                    </div>
                                  </Col>
                                  <Col md={4}>
                                    <div className="mb-3">
                                      <Label htmlFor="formrow-time-Input">
                                        Time*
                                      </Label>
                                      <input
                                        className="form-control"
                                        type="time"
                                        value={data.time}
                                        id="formrow-time-Input"
                                        onChange={e => {
                                          changeData({ time: e.target?.value })
                                        }}
                                      />
                                    </div>
                                  </Col>
                                </Row>
                                <Row>
                                  <Col md={12}>
                                    <div className="mb-3">
                                      <Label htmlFor="formrow-description-Input">
                                        Complaint/Additional Details
                                      </Label>
                                      <textarea
                                        className="form-control"
                                        rows={4}
                                        value={data.description}
                                        id="formrow-description-Input"
                                        onChange={e => {
                                          changeData({
                                            description: e.target?.value,
                                          })
                                        }}
                                      />
                                    </div>
                                  </Col>
                                </Row>
                              </Form>
                            </TabPane>
                            <TabPane tabId={3}>
                              <div
                                className="row justify-content-center"
                                style={
                                  submitted
                                    ? { minHeight: "300px" }
                                    : { minHeight: "200px" }
                                }
                              >
                                <Col lg="8">
                                  <div className="text-center">
                                    {submitted && (
                                      <div className="mb-4">
                                        <i className="mdi mdi-check-circle-outline text-success display-4" />
                                      </div>
                                    )}
                                    <div>
                                      <h5>
                                        {submitted
                                          ? "Appointment Requested Successfully!"
                                          : "Confirm Details"}
                                      </h5>
                                      {!submitted ? (
                                        <>
                                          <div className="text-muted">
                                            <p className="mb-3">
                                              Please confirm your appointment
                                              details:
                                            </p>
                                            <div className="text-start">
                                              <p>
                                                <strong>Name:</strong>{" "}
                                                {data.firstName} {data.lastName}
                                              </p>
                                              <p>
                                                <strong>Email:</strong>{" "}
                                                {data.email}
                                              </p>
                                              <p>
                                                <strong>Phone:</strong>{" "}
                                                {data.phoneNumber}
                                              </p>
                                              <p>
                                                <strong>Location:</strong>{" "}
                                                {getLocationName(data.location)}
                                              </p>
                                              <p>
                                                <strong>Service:</strong>{" "}
                                                {data.serviceText}
                                              </p>
                                              <p>
                                                <strong>Date & Time:</strong>{" "}
                                                {data.date} at {data.time}
                                              </p>
                                            </div>
                                          </div>
                                          <Alert
                                            color="success"
                                            tag="div"
                                            className="p-3"
                                            style={{
                                              backgroundColor: "#d4edda",
                                              borderColor: "#c3e6cb",
                                              color: "#155724",
                                            }}
                                          >
                                            <div className="d-flex align-items-start gap-3">
                                              {/* Hidden native checkbox */}
                                              <input
                                                type="checkbox"
                                                id="consentCheckbox"
                                                checked={consentToMarketing}
                                                onChange={e => setConsentToMarketing(e.target.checked)}
                                                style={{ display: "none" }}
                                              />

                                              {/* Custom visual checkbox */}
                                              <label
                                                htmlFor="consentCheckbox"
                                                style={{
                                                  width: "20px",
                                                  height: "20px",
                                                  minWidth: "20px",
                                                  borderRadius: "4px",
                                                  border: consentToMarketing ? "2px solid #155724" : "2px solid #aaa",
                                                  backgroundColor: consentToMarketing ? "#155724" : "#fff",
                                                  display: "flex",
                                                  alignItems: "center",
                                                  justifyContent: "center",
                                                  cursor: "pointer",
                                                  marginTop: "2px",
                                                  transition: "all 0.2s ease",
                                                }}
                                              >
                                                {consentToMarketing && (
                                                  <svg
                                                    width="12"
                                                    height="12"
                                                    viewBox="0 0 12 12"
                                                    fill="none"
                                                  >
                                                    <path
                                                      d="M2 6L5 9L10 3"
                                                      stroke="#fff"
                                                      strokeWidth="2"
                                                      strokeLinecap="round"
                                                      strokeLinejoin="round"
                                                    />
                                                  </svg>
                                                )}
                                              </label>

                                              {/* Label text */}
                                              <Label
                                                for="consentCheckbox"
                                                className="mb-0"
                                                style={{
                                                  textAlign: "justify",
                                                  color: "#155724",
                                                  cursor: "pointer",
                                                  lineHeight: "1.5",
                                                }}
                                              >
                                                By submitting this form, I consent to receive appointment
                                                confirmations, reminders, follow-ups, and promotional
                                                communications from Elpida Rehabilitation Center via phone call,
                                                WhatsApp, SMS, and email. You can opt out at any time.
                                              </Label>
                                            </div>
                                            {!consentToMarketing && (
                                              <p
                                                className="mb-0 mt-2 small"
                                                style={{ color: "#856404", paddingLeft: "30px" }}
                                              >
                                                ⚠️ You will only receive essential appointment updates.
                                              </p>
                                            )}
                                          </Alert>
                                        </>
                                      ) : (
                                        <p className="text-muted">
                                          Your appointment request has been sent
                                          successfully for {data.time} on{" "}
                                          {data.date} at our{" "}
                                          {getLocationName(data.location)}{" "}
                                          location. A confirmation email has
                                          been sent to {data.email}. Our team
                                          will contact you shortly to confirm
                                          your appointment.
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                </Col>
                              </div>
                            </TabPane>
                          </TabContent>
                        </div>
                      </div>
                      {!submitted && (
                        <div className="actions clearfix">
                          <ul>
                            <li
                              className={
                                activeTab === 1
                                  ? "previous disabled"
                                  : "previous"
                              }
                            >
                              {activeTab > 1 && (
                                <Link
                                  to="#"
                                  className="btn btn-primary"
                                  onClick={() => {
                                    if (!submitted) {
                                      toggleTab(activeTab - 1)
                                    }
                                  }}
                                >
                                  Previous
                                </Link>
                              )}
                            </li>
                            <li
                              className={`next ${submitted ? "disabled" : ""}`}
                            >
                              <Link
                                to="#"
                                className={`btn btn-primary ${activeTab === 3 ? "bg-success" : ""
                                  }`}
                                onClick={() => {
                                  if (activeTab === 3) {
                                    doSubmit()
                                  } else {
                                    if (levelVerified()) {
                                      toggleTab(activeTab + 1)
                                    }
                                  }
                                }}
                              >
                                {activeTab === 3 ? "Submit" : "Next"}
                              </Link>
                            </li>
                          </ul>
                        </div>
                      )}
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Container>
          <div
            className="position-fixed top-0 end-0 mt-5 m-1"
            style={{ zIndex: 1100 }}
          >
            {toasts.map(toast => (
              <Toast
                key={toast.id}
                isOpen={true}
                fade={true}
                className={`bg-${toast.type} rounded mb-2`}
              >
                <ToastHeader
                  toggle={() =>
                    setToasts(toasts.filter(t => t.id !== toast.id))
                  }
                >
                  <img src={logoSm} alt="" className="me-2" height="18" />
                  Elpida Rehab
                  <small className="text-muted ms-1">just now</small>
                </ToastHeader>
                <ToastBody>{toast.content}</ToastBody>
              </Toast>
            ))}
          </div>
        </div>
      )}
    </React.Fragment>
  )
}

export default AppointmentsPublic
